import React from 'react';
import styled from '@emotion/styled/macro';
import Translate from '@utils/translation-functions';
import vars from '@variables';
import { DownloadIconAtom } from '@atoms/icon-atoms/icon-atoms';
import { DownloadTileProps } from './download-tile.types';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { getImageParameters } from '../../mixins';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { H4Atom, PAtom } from '@atoms/text-atoms/text-atoms';
import { NoUnderlineLinkAtom } from '@atoms/link-atoms/link-atoms';

const DownloadTile: React.FC<DownloadTileProps> = ({
  downloadType,
  title,
  downloadSrc,
  iconType = 'downloadIcon',
  availableInMultipleLocales = false,
  enableLightText = false,
  backgroundImageSrc,
  gatsbyImageData,
  imageAlt = '',
  imageFormat,
  backgroundColour,
  translate = true,
  className,
}) => {
  const gatsbyImage = getImage(gatsbyImageData);
  if (downloadSrc) {
    return (
      <NoUnderlineLinkAtom
        href={downloadSrc}
        target="_blank"
        data-testid="download-link"
        data-gtm-id={GTM_ID.DOWNLOAD_LINK}
      >
        <div data-testid="download-tile" className={className}>
          {downloadType && (
            <PAtom data-testid="download-tile-dowload-type">
              {downloadType}
            </PAtom>
          )}
          {title && title.length > 0 && (
            <H4Atom data-testid="download-tile-title">{title}</H4Atom>
          )}
          {downloadSrc && (
            <div data-testid="download-icon" className="download">
              <DownloadIconAtom
                className={className}
                type={iconType}
                fill={enableLightText ? vars.white : vars.emfBlue}
              />
            </div>
          )}
          {availableInMultipleLocales && (
            <PAtom
              data-testid="available-in-multiple-locales"
              className="multiple-languages-message"
            >
              {Translate('multiple languages available', translate)}
            </PAtom>
          )}
          {gatsbyImage &&
          imageFormat !== 'image/gif' &&
          imageFormat !== 'image/svg+xml' ? (
            <GatsbyImage
              image={gatsbyImage}
              alt={imageAlt}
              className="gatsby-image-pos"
            />
          ) : (
            <div className="gatsby-image-wrapper gatsby-image-wrapper-constrained gatsby-image-pos">
              {backgroundImageSrc && (
                <img
                  src={getImageParameters(
                    backgroundImageSrc,
                    vars.defaultImageFormat,
                    452,
                    558
                  )}
                  alt={imageAlt}
                />
              )}
            </div>
          )}
        </div>
      </NoUnderlineLinkAtom>
    );
  }
  console.error(
    `Download tile called with invalid download src: ${downloadSrc}`
  );
  return null;
};

const StyledDownloadTile = styled(DownloadTile)`
  --download-tile-height: 27.9rem;
  --download-tile-width: 23.6rem;

  display: flex;
  text-decoration: none;
  height: var(--download-tile-height);
  width: var(--download-tile-width);
  position: relative;
  flex-direction: column;
  border: 1px solid ${vars.transparentBorderGrey};
  box-sizing: border-box;
  background-color: ${({ backgroundColour }) =>
    backgroundColour ? backgroundColour : 'transparent'};
  background-size: cover;
  background-repeat: no-repeat;
  overflow: hidden;
  border-radius: 8px;

  &:hover img {
    transform: scale(1.05);
  }

  ${PAtom} {
    font-weight: ${vars.fontWeightRegular};
    color: ${({ enableLightText }) =>
      enableLightText ? vars.white : vars.emfBlue};
    margin-top: ${vars.spacingM};
    margin-left: ${vars.spacingM};
    ${vars.fontSize2}
    padding-top: 0;
    padding-bottom: 1.1rem;
    z-index: 10;
    &::first-letter {
      text-transform: uppercase;
    }
  }

  .multiple-languages-message {
    max-width: 15rem;
    bottom: 0.6rem;
    position: absolute;
    color: ${({ enableLightText }) =>
      enableLightText ? vars.white : vars.emfBlue};
    z-index: 10;
  }
  .gatsby-image-pos {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    img {
      position: absolute;
      height: var(--download-tile-height);
      width: var(--download-tile-width);
      background-color: ${({ backgroundColour }) =>
        backgroundColour ? backgroundColour : 'white'};
      transition: transform 300ms ease-in-out;
    }
  }

  .download {
    width: 1.62rem;
    height: 1.82rem;
    position: absolute;
    bottom: 2.2rem;
    right: 1.7rem;
    background-color: transparent;
    display: block;
    border: 0;
    z-index: 10;
  }

  ${H4Atom} {
    margin-left: 1.5rem;
    max-width: 20.1rem;
    letter-spacing: -0.02rem;
    line-height: 1.3;
    ${vars.fontSize5}
    font-weight: ${vars.fontWeightBold};
    color: ${({ enableLightText }) =>
      enableLightText ? vars.white : vars.emfBlue};
    letter-spacing: -0.02em;
    z-index: 10;
  }
`;

export default StyledDownloadTile;
